import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import LineBathIcon from 'components/Luxkit/Icons/line/LineBathIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LineVillaIcon from 'components/Luxkit/Icons/line/LineVillaIcon'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import Heading from 'components/Luxkit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { nonNullable } from 'lib/array/arrayUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import moment from 'moment'
import { rem } from 'polished'
import React, { useContext, useMemo } from 'react'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import useShareModalConfig from 'hooks/useShareModalConfig'
import { getPropertyCategory } from 'lib/homesAndVillas/getPropertyCategory'
import ContactUSP from '../ContactUSP'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
    grid-template-columns: max-content;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
  }
`

interface Props {
  offer: App.VillaOffer;
  onViewLocationClick?: () => void;
  onViewReviewsClick: () => void;
  pkg: App.HotelPackage;
  checkIn?: moment.Moment;
  duration: number;
  rooms: Array<App.Occupants>;
}

function VillasPageIntro(props: Props) {
  const { offer, onViewLocationClick, onViewReviewsClick, checkIn, pkg, rooms, duration } = props
  const { property } = offer
  const { currentRegionCode } = useContext(GeoContext)

  const maxGuests = useMemo(() => {
    const allCapacities = offer.packages.flatMap(p => p.roomRate?.capacities ?? [])
    const max = getMaxOccupancies(allCapacities, { adults: 0 }, { maxChildAge: offer.property?.maxChildAge, maxInfantAge: offer.property?.maxInfantAge })
    return max.adults
  }, [offer])

  const showUserReviews = useMemo(() => {
    return property && showUserReviewsRating(property.rating, property.reviewsTotal)
  }, [property])

  const propertyCategory = useMemo(() => getPropertyCategory(property.category), [property])

  const shareConfig = useShareModalConfig({
    type: 'offer',
    offerId: offer.id,
    image: offer.images[0],
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
  })
  return (
    <Group fullWidth direction="vertical" gap={20} tabletGap={24}>
      <Group direction="vertical" gap={12}>
        <TripGuard>
          <CSSBreakpoint max="mobile" >
            <Group direction="horizontal" verticalAlign="center" gap={12} horizontalAlign="end" >
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </Group>
          </CSSBreakpoint>
        </TripGuard>
        <VerticalSpacer gap={16}>
          <LabelGroup>
            <OfferLabels offer={offer} showLimitedAvailability />
            {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
              <PromotionLabel variant="default" icon={<LineUserIcon />}>
                24/7 support
              </PromotionLabel>
            )}
          </LabelGroup>
          <VerticalSpacer gap={12}>
            <Group direction="horizontal" verticalAlign="center" gap={4}>
              <BodyTextBlock
                data-testid="offer-location"
                variant="medium"
              >
                {nonNullable([offer.locationHeading, offer.locationSubheading]).join(', ')} &#x2027;
              </BodyTextBlock>
              <TextLink size="medium" onClick={onViewLocationClick}>View location</TextLink>
            </Group>
            <Heading variant="heading2">{offer.property.name}</Heading>
          </VerticalSpacer>
        </VerticalSpacer>
        {showUserReviews && (
          <NumberRating
              link
              inlineLabel
              variant="small"
              total={property.reviewsTotal}
              rating={property.rating}
              ratingSubtitle={
                property.reviewsSource === 'google' ? 'From online reviews' : undefined
              }
              onReviewSubTitleClick={onViewReviewsClick}
            />
        )}
      </Group>
      <Group direction="vertical" gap={16}>
        <Divider kind="primary" />
        <SummaryGrid>
          <BodyTextBlock variant="medium" startIcon={<LineVillaIcon />}>
            {propertyCategory}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
            Up to {pluralizeToString('guest', maxGuests)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBedIcon />}>
            {pluralizeToString('bedroom', offer.property.bedrooms)}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineBathIcon />}>
            {pluralizeToString('bathroom', offer.property.bathrooms)}
          </BodyTextBlock>
        </SummaryGrid>
        <Divider kind="primary" />
      </Group>
      {checkIn && <CSSBreakpoint max="tablet">
        <TAOCancellationPolicies
          offerId={offer.id}
          useDynamicCancellationPolicies={offer.property.useDynamicCancellationPolicies}
          timezoneOffset={offer.property.timezoneOffset}
          uniqueKey={pkg.uniqueKey}
          checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
          checkOutDate={checkIn ? checkIn.clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined}
          occupants={rooms}
          cancellationPolicy={pkg.roomRate.cancellationPolicy}
          hideExcludesFlights
          offerType={offer.type}
        />
      </CSSBreakpoint>}
      <div>
        <LargeContentToggle maxHeight={120} showChevron showLessText="Read less" showMoreText="Read more">
          <MarkdownRender type="compact" content={offer.description} />
        </LargeContentToggle>
      </div>
      <ContactUSP />
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    pkg: getCheapestSelectedPackageOptionWithDefault(appState, state).package,
    checkIn: state.checkInDate,
    duration: state.duration,
    rooms: state.rooms,
  }
})(VillasPageIntro)
